import React from 'react';
import {
  ContentfulCarouselBanner,
  ContentfulPercentOffBanner,
  ContentfulProductBanner,
  ContentfulSupplierBanner,
  ProgressBarThemeType,
} from '@jbhi-fi/apis/src/contentful/types';
import { ContentfulImage } from '../../ContentfulImage/ContentfulImage';
import { AssetTransformer } from './AssetTransformer';
import { HeroBannerProps } from '../../HeroBanner/HeroBanner';
import { HeroBannerCarouselProps } from '../../HeroBannerCarousel/HeroBannerCarousel';
import { ThemeTransformer } from './ThemeTransformer';
import { palette } from '@jbhi-fi/lanyard-ui/themes';
import { PercentOffBannerTransformer } from './PercentOffBannerTransformer';
import { ProductBannerTransformer } from './ProductBannerTransformer';
import { SupplierProductBannerTransformer } from './SupplierProductBannerTransformer';

export abstract class CarouselBannerTransformer {
  static toHeroBannerProps = (
    entry?: ContentfulCarouselBanner,
    options?: { variant?: 'homepage' | 'collection' | 'genericPage' }
  ): { isLoading?: false } & HeroBannerProps => {
    const useDynamicImageUrls = entry?.fields?.dynamicImageUrls;
    const hasHeroBanner = !!(useDynamicImageUrls
      ? entry?.fields?.desktopHeroBannerUrl
      : entry?.fields?.desktopHeroBanner);
    const hasLegacyBanner = !!entry?.fields?.desktopImage;
    const shouldUseHeroBanner =
      (hasHeroBanner && (!hasLegacyBanner || options?.variant !== 'collection')) || options?.variant === 'genericPage';
    return {
      href: entry?.fields?.urllink ?? '',
      isContained: (shouldUseHeroBanner || options?.variant === 'genericPage') && entry?.fields?.hasBorder,
      isHeroBanner: shouldUseHeroBanner,
      image: useDynamicImageUrls ? (
        <img src={entry?.fields?.desktopHeroBannerUrl} loading="eager" alt="Banner" />
      ) : (
        <ContentfulImage
          {...AssetTransformer.toContentfulImageProps(
            shouldUseHeroBanner ? entry?.fields?.desktopHeroBanner : entry?.fields?.desktopImage
          )}
          loading="eager"
          sizes="(max-width: 1124px) 100vw, 2324px"
          isProgressive={false}
        />
      ),
      imageMobile: useDynamicImageUrls ? (
        <img src={entry.fields?.mobileHeroBannerUrl} loading="eager" alt="Banner" />
      ) : (
        <ContentfulImage
          {...AssetTransformer.toContentfulImageProps(entry?.fields?.mobileHeroBanner)}
          loading="eager"
          sizes="(max-width: 1124px) 100vw, 2324px"
          isProgressive={false}
        />
      ),
      trackingData: {
        id: entry?.fields?.name ?? '',
        url: entry?.fields?.urllink,
        type: 'carousel',
        brand: entry?.fields?.brand,
      },
    };
  };

  static toHeroBannerCarouselProps = (
    banners: Array<{
      entry: ContentfulCarouselBanner | ContentfulPercentOffBanner | ContentfulProductBanner | ContentfulSupplierBanner;
      campaignName: string;
      category: string;
    }>,
    options?: { variant?: 'homepage' | 'collection' | 'genericPage' }
  ): HeroBannerCarouselProps => {
    return {
      banners: banners
        .filter(banner => banner?.entry?.fields)
        .map((banner, i) => {
          if (isPercentOffBanner(banner.entry)) {
            const bannerProps = PercentOffBannerTransformer.toPercentOffHtmlBannerProps(banner.entry);
            return {
              type: 'percentOffBanner',
              ...bannerProps,
              trackingData: {
                ...bannerProps.trackingData,
                id: bannerProps.trackingData?.id || '',
                position: i + 1,
                campaign: banner.campaignName,
                category: banner.category,
              },
              bannerTheme: {
                background: palette.primary.main,
                paginationActive: palette.primary.dark,
                arrow: palette.primary.dark,
              },
            };
          } else if (isProductBanner(banner.entry)) {
            const bannerProps = ProductBannerTransformer.toProductHtmlBannerProps(banner.entry);
            return {
              type: 'productBanner',
              ...bannerProps,
              trackingData: {
                ...bannerProps.trackingData,
                id: bannerProps.trackingData?.id || '',
                position: i + 1,
                campaign: banner.campaignName,
                category: banner.category,
              },
              bannerTheme: {
                background: palette.primary.main,
                paginationActive: palette.primary.dark,
                arrow: palette.primary.dark,
              },
            };
          } else if (isSupplierBanner(banner.entry)) {
            const bannerProps = SupplierProductBannerTransformer.toSupplierProductBannerProps(banner.entry);
            return {
              type: 'supplierBanner',
              ...bannerProps,
              trackingData: {
                ...bannerProps.trackingData,
                id: bannerProps.trackingData?.id || '',
                position: i + 1,
                campaign: banner.campaignName,
                category: banner.category,
              },
              bannerTheme: {
                background: palette.primary.main,
                paginationActive: palette.primary.dark,
                arrow: palette.primary.dark,
              },
            };
          } else {
            const bannerProps = CarouselBannerTransformer.toHeroBannerProps(banner.entry, options);
            return {
              type: 'heroBanner',
              ...bannerProps,
              trackingData: {
                ...bannerProps.trackingData,
                id: bannerProps.trackingData?.id || '',
                position: i + 1,
                campaign: banner.campaignName,
                category: banner.category,
              },
              bannerTheme: {
                background:
                  ThemeTransformer.toContentThemePalette(banner.entry.fields?.backgroundColour).background ??
                  palette.primary.main,
                paginationActive:
                  banner.entry.fields?.progressBarTheme === ProgressBarThemeType.Dark
                    ? palette.primary.main
                    : undefined,
                arrow:
                  banner.entry.fields?.progressBarTheme === ProgressBarThemeType.Dark
                    ? palette.primary.main
                    : undefined,
              },
            };
          }
        }),
    };
  };
}

export const isPercentOffBanner = (
  item: ContentfulCarouselBanner | ContentfulPercentOffBanner | ContentfulProductBanner | ContentfulSupplierBanner
): item is ContentfulPercentOffBanner => item?.sys?.contentType?.sys?.id === 'percentOffBanner';

export const isProductBanner = (
  item: ContentfulCarouselBanner | ContentfulPercentOffBanner | ContentfulProductBanner | ContentfulSupplierBanner
): item is ContentfulProductBanner => item?.sys?.contentType?.sys?.id === 'productBanner';

export const isSupplierBanner = (
  item: ContentfulCarouselBanner | ContentfulPercentOffBanner | ContentfulProductBanner | ContentfulSupplierBanner
): item is ContentfulSupplierBanner => item?.sys?.contentType?.sys?.id === 'supplierBanner';
